import { useState, useEffect, useCallback } from "react";
import { getLogos } from "./brandApi";

// Module-level cache so components mounted at the same time share one request
// instead of each firing its own.
const cache = new Map();     // email -> logos array
const inflight = new Map();  // email -> in-flight promise
const listeners = new Set(); // (email) => void

const emit = (email) => listeners.forEach((fn) => fn(email));

// Refetch and tell every mounted consumer. Call after create/update/delete
// so the account modal and the carousel picker stay in sync.
export const refreshLogosFor = async (email) => {
  if (!email) return;
  inflight.delete(email);
  try {
    const data = await getLogos(email);
    cache.set(email, data);
  } catch (err) {
    console.error("Failed to refresh logos:", err);
    cache.delete(email);
  }
  emit(email);
};

export default function useLogos(email) {
  const [logos, setLogos] = useState(() => cache.get(email) || []);
  const [loading, setLoading] = useState(!cache.has(email));
  const [error, setError] = useState("");

  const load = useCallback(async (force = false) => {
    if (!email) {
      setLogos([]);
      setLoading(false);
      return;
    }
    if (!force && cache.has(email)) {
      setLogos(cache.get(email));
      setLoading(false);
      return;
    }
    if (force) inflight.delete(email);

    setLoading(true);
    setError("");
    try {
      if (!inflight.has(email)) inflight.set(email, getLogos(email));
      const data = await inflight.get(email);
      cache.set(email, data);
      setLogos(data);
    } catch (err) {
      console.error("Failed to load logos:", err);
      setError(err.message);
      setLogos([]);
    } finally {
      inflight.delete(email);
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { load(); }, [load]);

  // Stay in sync when another component triggers a refresh.
  useEffect(() => {
    const onChange = (changed) => {
      if (changed !== email) return;
      setLogos(cache.get(email) || []);
    };
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, [email]);

  return { logos, loading, error, refresh: () => load(true) };
}